import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, Sparkles, AlertCircle } from 'lucide-react';
import { Logo } from './Logo';
import { useAuth } from '../context/AuthContext';
import { apiGet, apiPost } from '../utils/apiClient';
import { TRIAL_DAYS } from '../utils/planPricing';

export function HouseholdAccessEnded() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState('');
  const [errorMessage, setErrorMessage] = useState(location.state?.loadError || '');

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const data = await apiGet('/user/me');
        if (!cancelled) {
          setProfile(data);
        }
      } catch (err) {
        console.warn('Failed to load household access transition:', err);
        if (!cancelled) {
          setErrorMessage('Unable to load your account status right now.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const transition = profile?.householdAccessTransition || null;

  useEffect(() => {
    if (!loading && !transition) {
      navigate('/MealPlan', { replace: true });
    }
  }, [loading, transition, navigate]);

  const ownerName = (transition?.ownerName || '').trim();
  const ownerFirstName = ownerName ? ownerName.split(/\s+/)[0] : '';
  const householdLabel = transition?.householdName || (ownerFirstName ? `${ownerFirstName}'s household` : 'your household');
  const canStartTrial = transition?.canStartTrial !== false;

  const handleOpenPlans = () => {
    navigate('/household-access/start-trial');
  };

  const handleContinue = async () => {
    try {
      setSubmitting('continue');
      setErrorMessage('');
      await apiPost('/household-access/continue', {});
      navigate('/MealPlan', { replace: true });
    } catch (err) {
      console.warn('Failed to continue after household access ended:', err);
      setErrorMessage(err?.message || 'Could not continue right now. Please try again.');
    } finally {
      setSubmitting('');
    }
  };

  const handleNotNow = async () => {
    try {
      setSubmitting('later');
      setErrorMessage('');
      await apiPost('/household-access/not-now', {});
      await signOut();
      navigate('/auth/signin', { replace: true });
    } catch (err) {
      console.warn('Failed to pause household access transition:', err);
      setErrorMessage(err?.message || 'Could not finish that right now. Please try again.');
    } finally {
      setSubmitting('');
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-[#F8F7F5]" />;
  }

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,#E9F0DD_0%,#F8F7F5_42%,#F3F1EC_100%)] px-6 py-10">
      <div className="mx-auto max-w-md">
        {errorMessage && (
          <div className="mb-4 rounded-2xl border border-[#B85A5A]/30 bg-[#FEF5F5] px-4 py-3 text-sm text-[#B85A5A]">
            <div className="flex items-start gap-2">
              <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#B85A5A]" />
              <p>{errorMessage}</p>
            </div>
          </div>
        )}

        <div className="rounded-[32px] border border-black/10 bg-white/90 p-6 shadow-[0_24px_80px_rgba(31,40,20,0.12)] backdrop-blur">
          <div className="mx-auto mb-8 flex h-20 w-20 items-center justify-center rounded-full bg-[#FFF4DE] shadow-sm">
            <Logo className="h-10 w-10" color="#AFBB8B" />
          </div>

          <div className="text-center">
            <p className="mb-3 text-sm font-semibold uppercase tracking-[0.22em] text-[#6D7368]">MealPlan Genie</p>
            <h1 className="mb-4 text-[34px] font-semibold leading-[1.05] text-[#1F2814]">
              Your household access has ended
            </h1>
            <p className="mx-auto mb-3 max-w-[28ch] text-[18px] leading-7 text-[#334128]">
              You no longer have access to {householdLabel}.
            </p>
            <p className="mx-auto mb-8 max-w-[30ch] text-[16px] leading-7 text-[#5A6252]">
              Your account is still here. If you want to keep using MealPlan Genie,
              you can continue on your own terms.
            </p>
          </div>

          {!canStartTrial && (
            <button
              onClick={handleContinue}
              disabled={submitting !== ''}
              className="mb-4 block w-full rounded-[18px] bg-[#E6F0DE] px-5 py-5 text-left text-[#1F2814] transition hover:bg-[#DCE8D1] disabled:opacity-60"
            >
              <span className="mb-2 block text-[17px] font-semibold">
                Continue with your own plan
              </span>
              <span className="block text-[15px] leading-6 text-[#536048]">
                Create your own household and keep meal planning, shopping lists, and recipes on your own terms.
              </span>
            </button>
          )}

          {canStartTrial && (
            <button
              onClick={handleContinue}
              disabled={submitting !== ''}
              className="mb-4 block w-full rounded-[18px] bg-[#E6F0DE] px-5 py-5 text-left text-[#1F2814] transition hover:bg-[#DCE8D1] disabled:opacity-60"
            >
              <span className="mb-2 block text-[17px] font-semibold">
                Continue with your own plan
              </span>
              <span className="block text-[15px] leading-6 text-[#536048]">
                Skip pricing for now and create your own household with your trial ready to start.
              </span>
            </button>
          )}

          {canStartTrial && (
            <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-[#F0F6E8] px-4 py-2 text-sm font-medium text-[#3E5A2C]">
              <Sparkles className="h-4 w-4" />
              <span>{TRIAL_DAYS} days free. No credit card required.</span>
            </div>
          )}

          {canStartTrial && (
            <button
              onClick={handleOpenPlans}
              disabled={submitting !== ''}
              className="mb-3 w-full rounded-[18px] bg-[#AFBB8B] px-5 py-4 text-[17px] font-semibold text-[#1F2814] transition hover:bg-[#9FAA7B] disabled:opacity-60"
            >
              Start free trial
            </button>
          )}

          <button
            onClick={handleNotNow}
            disabled={submitting !== ''}
            className="mb-4 w-full rounded-[18px] border border-[#C9D1C2] bg-transparent px-5 py-4 text-[17px] font-medium text-[#1F2814] transition hover:bg-[#F8F7F5] disabled:opacity-60"
          >
            Not right now
          </button>

          <p className="text-center text-sm leading-6 text-[#6D7368]">
            {canStartTrial
              ? 'No credit card needed to start. Cancel anytime.'
              : 'You can come back later and start your own plan when you are ready.'}
          </p>
        </div>

        <button
          onClick={handleNotNow}
          disabled={submitting !== ''}
          className="mx-auto mt-5 flex items-center gap-2 text-sm font-medium text-[#58723F] underline-offset-4 hover:underline disabled:opacity-60"
        >
          Sign out for now
          <ArrowRight className="h-4 w-4" />
        </button>

        {/* Someone who does not want to subscribe must still be able to take their data and
            remove their account. Without this the only ways off this screen are starting a
            trial or signing out, which leaves no route to Delete Account at all. */}
        <button
          onClick={() => navigate('/More')}
          disabled={submitting !== ''}
          className="mx-auto mt-4 block text-sm text-[#6D7368] underline underline-offset-4 hover:text-[#1F2814] disabled:opacity-60"
        >
          Manage or delete your account
        </button>
      </div>
    </div>
  );
}

export default HouseholdAccessEnded;